/** Profile settings section — avatar upload, display name, and bio editing. */
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'motion/react';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { UpdateProfileSchema, COUNTRIES, getInitials } from '@formency/shared';
import { Button, FileTrigger, FormTextField, FormComboBox, Form } from '@/interfaces/learner/ui';
import { useProfile, useUpdateProfile, useUploadAvatar, useDeleteAvatar } from '@/shared/api/hooks';
import { useAuth } from '@/shared/auth/AuthProvider';
import { extractErrorMessage } from '@/shared/utils/extractErrorMessage';
import { SettingsSection } from './SettingsSection';

const EASE = [0.16, 1, 0.3, 1] as const;

const ProfileFormSchema = UpdateProfileSchema.pick({
  name: true,
  mobile: true,
  country: true,
});

type ProfileFormData = z.infer<typeof ProfileFormSchema>;

export default function ProfileSection() {
  const { user } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const updateMutation = useUpdateProfile();
  const uploadMutation = useUploadAvatar();
  const deleteMutation = useDeleteAvatar();

  const countryItems = useMemo(() => COUNTRIES.map((c) => ({ id: c.code, label: c.label })), []);

  const formValues: ProfileFormData = {
    // Prefer the canonical user.name (BetterAuth session) but fall back to
    // profile.name (returned by /me/profile) for the brief render before
    // the BetterAuth session settles.
    name: user?.name ?? profile?.name ?? '',
    mobile: profile?.mobile ?? '',
    country: (profile?.country as ProfileFormData['country']) ?? undefined,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(ProfileFormSchema),
    values: formValues,
    mode: 'onBlur',
  });

  function onSubmit(data: ProfileFormData) {
    const payload: Record<string, string | undefined> = {};
    if (dirtyFields.name) payload.name = data.name;
    if (dirtyFields.mobile) payload.mobile = data.mobile;
    if (dirtyFields.country) payload.country = data.country;

    updateMutation.mutate(payload, {
      onSuccess: () => toast.success('Profile updated'),
      onError: (error: unknown) =>
        toast.error(extractErrorMessage(error, 'Failed to update profile')),
    });
  }

  function handleFileSelect(files: FileList | null) {
    const file = files?.[0];
    if (file) {
      uploadMutation.mutate(file, {
        onSuccess: () => toast.success('Profile picture updated'),
        onError: (error: unknown) =>
          toast.error(extractErrorMessage(error, 'Failed to upload picture')),
      });
    }
  }

  function handleDeleteAvatar() {
    deleteMutation.mutate(undefined, {
      onSuccess: () => toast.success('Profile picture removed'),
      onError: (error: unknown) =>
        toast.error(extractErrorMessage(error, 'Failed to remove picture')),
    });
  }

  const avatarUrl = profile?.avatarUrl;
  const isUploading = uploadMutation.isPending;
  const isDeleting = deleteMutation.isPending;
  const userInitial = getInitials({
    name: user?.name ?? profile?.name ?? null,
    email: user?.email ?? null,
  });

  if (isLoading) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
      >
        <div className="animate-pulse space-y-4">
          <div
            className="h-6 w-24 rounded-md"
            style={{ background: 'var(--color-surface-muted)' }}
          />
          <div className="flex gap-6">
            <div
              className="h-20 w-20 rounded-full shrink-0"
              style={{ background: 'var(--color-surface-muted)' }}
            />
            <div className="flex-1 space-y-3 pt-2">
              <div
                className="h-4 w-48 rounded"
                style={{ background: 'var(--color-surface-muted)' }}
              />
              <div
                className="h-4 w-32 rounded"
                style={{ background: 'var(--color-surface-muted)' }}
              />
            </div>
          </div>
        </div>
      </motion.section>
    );
  }

  return (
    <SettingsSection title="Profile">
      {/* Avatar row */}
      <div className="flex flex-col gap-6 learner-single:flex-row">
        {/* Avatar + actions */}
        <div className="flex flex-col items-center gap-2.5 shrink-0">
          <div
            className="relative h-20 w-20 rounded-full overflow-hidden"
            style={{
              background: avatarUrl ? undefined : 'var(--color-accent-subtle)',
            }}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profile" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center">
                <span
                  className="text-2xl font-semibold select-none"
                  style={{ color: 'var(--color-accent-text)' }}
                >
                  {userInitial}
                </span>
              </div>
            )}
            {isUploading && (
              <div
                className="absolute inset-0 flex items-center justify-center rounded-full"
                style={{ background: 'color-mix(in srgb, var(--color-overlay) 72%, transparent)' }}
              >
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <FileTrigger
              acceptedFileTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp']}
              onSelect={handleFileSelect}
            >
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon={<Upload size={13} strokeWidth={2} />}
                isDisabled={isUploading}
                style={{ color: 'var(--color-accent-text)' }}
              >
                Upload
              </Button>
            </FileTrigger>
            {avatarUrl && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon={
                  isDeleting ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <X size={13} strokeWidth={2} />
                  )
                }
                onPress={handleDeleteAvatar}
                isDisabled={isDeleting}
                style={{ color: 'var(--color-error-text)' }}
              >
                Remove
              </Button>
            )}
          </div>
        </div>

        {/* Form fields */}
        <Form onSubmit={handleSubmit(onSubmit)} className="flex-1 space-y-4">
          <FormTextField
            control={control}
            name="name"
            label="Name"
            placeholder="What should we call you?"
            description="One field — type your full name, or whatever you want to be addressed as"
          />
          <div className="grid grid-cols-1 gap-4 learner-single:grid-cols-2">
            <FormTextField
              control={control}
              name="mobile"
              label="Mobile"
              placeholder="Mobile number"
            />
            <FormComboBox
              control={control}
              name="country"
              label="Country"
              placeholder="Search countries..."
              items={countryItems}
            />
          </div>

          {isDirty && (
            <div className="flex gap-3 pt-1">
              <Button type="submit" isDisabled={updateMutation.isPending} variant="accent">
                {updateMutation.isPending ? 'Saving...' : 'Save changes'}
              </Button>
              <Button
                onPress={() => {
                  reset();
                  updateMutation.reset();
                }}
                isDisabled={updateMutation.isPending}
                variant="ghost"
              >
                Cancel
              </Button>
            </div>
          )}
        </Form>
      </div>
    </SettingsSection>
  );
}
